import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Heart, ArrowUpRight, Ruler, Sparkles, Shield } from "lucide-react";
import { findCollection, COLLECTIONS, PRICE } from "../data/collections";
import { whatsappLink, colorOf, PERSONALITIES } from "../data/site";
import { useApp } from "../context/AppContext";
import { WhatsAppIcon, Squiggle, SunDoodle, Star } from "../components/Illustrations";
import { PrimaryDoodle, SecondaryDoodle } from "../components/PersonalityDoodles";
import MagneticButton from "../components/MagneticButton";
import Reveal from "../components/Reveal";
import ZoomableImage from "../components/ZoomableImage";
import COLOR_VARIANTS from "../data/color-variants.json";

export default function CollectionDetail() {
  const { slug } = useParams();
  const c = findCollection(slug);
  const [activeColor, setActiveColor] = useState(c?.colors[0]);
  const [activeImg, setActiveImg] = useState("kid");
  const [colorPreview, setColorPreview] = useState(true);

  useEffect(() => {
    if (c) {
      setActiveColor(c.colors[0]);
      setActiveImg("kid");
      setColorPreview(true);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [slug, c, setActiveColor, setActiveImg, setColorPreview]);

  const { isWished, toggleWish } = useApp();

  if (!c) return <Navigate to="/collections" replace />;

  const wished = isWished(c.slug);

  const buyMsg = `Hi SOL Kids! I'd love to order the ${c.name} sunglasses in ${activeColor.toUpperCase()} colour. (${PRICE})`;
  const personality = PERSONALITIES.find((p) => p.slug === c.personality);
  const related = COLLECTIONS.filter((x) => x.slug !== c.slug && x.personality === c.personality).slice(0, 4);
  const fallback = COLLECTIONS.filter((x) => x.slug !== c.slug).slice(0, 4);
  const recs = related.length >= 3 ? related : [...related, ...fallback].slice(0, 4);

  const images = [
    { key: "kid", src: `/products/${c.slug}-kid.jpg`, alt: `${c.name} on a kid` },
    { key: "swatches", src: `/products/${c.slug}-swatches.jpg`, alt: `${c.name} colour variants` },
    { key: "full", src: `/products/${c.slug}-full.jpg`, alt: `${c.name} look book page` },
    { key: "hero", src: `/products/${c.slug}-hero.jpg`, alt: `${c.name} hero` },
  ];

  // Per-color frame crop (from lookbook swatches) — drives the "colour" thumbnail.
  const variants = COLOR_VARIANTS[c.slug]?.variants || {};
  const activeVariant = variants[activeColor];
  // The main image: color preview wins unless user explicitly clicks a thumbnail.
  const showVariant = colorPreview && !!activeVariant;
  const mainSrc = showVariant ? activeVariant : images.find((x) => x.key === activeImg).src;
  const mainAlt = showVariant
    ? `${c.name} in ${activeColor}`
    : images.find((x) => x.key === activeImg).alt;

  return (
    <div className="pt-24 md:pt-28 pb-32" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 mb-6 text-[12px] text-[#595955] flex items-center gap-2">
        <Link to="/collections" className="ink-link"><ArrowLeft className="w-3.5 h-3.5 inline -mt-0.5" /> Back to all</Link>
        <span>/</span>
        <span className="font-serif text-[#2C2C2A] text-[15px]">{c.name}</span>
      </div>

      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-14">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <Reveal>
            <ZoomableImage
              key={mainSrc}
              src={mainSrc}
              alt={mainAlt}
              className="aspect-[4/5] rounded-[28px] bg-[#F3EADA] transition-opacity duration-200"
              zoom={2}
              fit={showVariant ? "contain" : "cover"}
              data-testid="product-main-image"
            >
              <div className="absolute top-5 left-5 pointer-events-none z-10">
                <PrimaryDoodle personality={c.personality} className="!w-12 !h-12" />
              </div>
              <SecondaryDoodle personality={c.personality} className="!absolute !bottom-5 !right-5 opacity-90 pointer-events-none z-10" />
              <Star size={20} className="absolute top-5 right-5 wiggle pointer-events-none z-10" />
              {showVariant && (
                <div className="absolute top-5 left-1/2 -translate-x-1/2 pointer-events-none z-10 rounded-full bg-[#FAFAF7]/95 backdrop-blur px-3 py-1.5 text-[11px] font-medium tracking-[0.05em] text-[#2C2C2A]">
                  Showing — <span className="capitalize">{activeColor}</span>
                </div>
              )}
            </ZoomableImage>
          </Reveal>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {images.map((im) => (
              <button
                key={im.key}
                onClick={() => { setActiveImg(im.key); setColorPreview(false); }}
                className={`aspect-square rounded-xl overflow-hidden border ${!showVariant && activeImg === im.key ? "border-[#2C2C2A] ring-1 ring-[#2C2C2A]" : "border-[#2C2C2A]/15"}`}
                data-testid={`thumb-${im.key}`}
                aria-label={`View ${im.key}`}
              >
                <img src={im.src} alt={im.alt} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <Reveal>
            <p className="eyebrow mb-3">{c.audience}</p>
          </Reveal>
          <Reveal delay={60}>
            <h1 className="font-serif text-6xl md:text-7xl leading-none tracking-[-0.03em]" data-testid="product-title">{c.name}</h1>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-5 space-y-1.5 text-[#2C2C2A]/85 text-lg md:text-xl leading-snug">
              {c.tagline.map((t) => <p key={t}>{t}</p>)}
            </div>
          </Reveal>
          <Reveal delay={180}>
            <div className="mt-6 flex items-center gap-4">
              <span className="font-serif text-3xl" data-testid="product-price">{PRICE}</span>
              <span className="text-[12px] text-[#595955] uppercase tracking-[0.2em]">Shipping charges extra, as applicable</span>
            </div>
          </Reveal>

          {/* Color swatches */}
          <Reveal delay={220}>
            <div className="mt-9">
              <div className="flex items-center justify-between mb-3">
                <p className="eyebrow">Colour <span className="text-[#2C2C2A] tracking-normal normal-case font-medium text-[14px] ml-2">{activeColor}</span></p>
                <span className="text-[12px] text-[#595955]">{c.colors.length} variants</span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {c.colors.map((col) => {
                  const hasPreview = !!variants[col];
                  return (
                    <button
                      key={col}
                      onClick={() => { setActiveColor(col); setColorPreview(true); }}
                      className={`relative w-10 h-10 rounded-full transition-all ${activeColor === col ? "ring-2 ring-[#2C2C2A] ring-offset-2 ring-offset-[#FAFAF7]" : "ring-1 ring-[#2C2C2A]/20 hover:scale-105"}`}
                      style={{ background: colorOf(col) }}
                      title={hasPreview ? `${col} — preview available` : col}
                      aria-label={col}
                      data-testid={`swatch-${col.replace(/\s+/g, "-")}`}
                    >
                      {hasPreview && (
                        <span
                          aria-hidden
                          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#2C2C2A] border-2 border-[#FAFAF7] flex items-center justify-center"
                          title="Preview available"
                        >
                          <span className="w-1 h-1 rounded-full bg-[#FAFAF7]" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </Reveal>

          {/* CTAs */}
          <Reveal delay={280}>
            <div className="mt-8 flex flex-wrap gap-3">
              <MagneticButton className="flex-1 min-w-[230px]">
                <a
                  href={whatsappLink(buyMsg)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-4 text-[15px] font-medium hover:brightness-95 transition-all"
                  data-testid="product-buy-whatsapp"
                >
                  <WhatsAppIcon /> Buy now on WhatsApp
                </a>
              </MagneticButton>
              <button
                onClick={() => toggleWish(c.slug)}
                className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-4 text-[14px] font-medium border transition-all ${wished ? "bg-[#2C2C2A] text-[#FAFAF7] border-[#2C2C2A]" : "bg-transparent text-[#2C2C2A] border-[#2C2C2A]/20 hover:border-[#2C2C2A]"}`}
                data-testid="product-wishlist-toggle"
              >
                <Heart className={`w-4 h-4 ${wished ? "fill-current" : ""}`} />
                {wished ? "Saved" : "Save"}
              </button>
            </div>
          </Reveal>

          {/* Features mini grid */}
          <Reveal delay={340}>
            <div className="mt-10 grid grid-cols-3 gap-3 text-[12px]">
              <div className="rounded-xl bg-[#F3EADA] px-3 py-3"><Shield className="w-4 h-4 mb-1.5" /><div>Polarized</div></div>
              <div className="rounded-xl bg-[#F3EADA] px-3 py-3"><Sparkles className="w-4 h-4 mb-1.5" /><div>100% UV</div></div>
              <div className="rounded-xl bg-[#F3EADA] px-3 py-3"><Ruler className="w-4 h-4 mb-1.5" /><div>Flex-frame</div></div>
            </div>
          </Reveal>

          {/* Specs */}
          <Reveal delay={400}>
            <div className="mt-10 border-t border-[#2C2C2A]/10 pt-6 text-[14px]">
              <div className="grid grid-cols-3 gap-y-4 gap-x-6">
                <div>
                  <p className="eyebrow mb-1">Frame</p>
                  <p>{c.dims.width} cm wide</p>
                </div>
                <div>
                  <p className="eyebrow mb-1">Bridge</p>
                  <p>{c.dims.bridge} cm</p>
                </div>
                <div>
                  <p className="eyebrow mb-1">Arm</p>
                  <p>{c.dims.arm} cm</p>
                </div>
                <div>
                  <p className="eyebrow mb-1">Age fit</p>
                  <p>{c.age} years</p>
                </div>
                <div className="col-span-2">
                  <p className="eyebrow mb-1">Personality</p>
                  {personality && (
                    <Link to={`/personality/${personality.slug}`} className="ink-link">
                      {personality.emoji} {personality.title}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </Reveal>

          <Squiggle w={140} className="mt-10 opacity-50" />
          <p className="mt-6 text-[12px] text-[#595955]">
            Ships in 3–5 working days · Shipping charges extra, as applicable · 7-day exchange · 6-month warranty
          </p>
        </div>
      </div>

      {/* Recommended */}
      <section className="mx-auto max-w-[1440px] px-5 md:px-10 mt-28">
        <div className="flex items-end justify-between gap-6 mb-10">
          <h2 className="font-serif text-3xl md:text-5xl tracking-[-0.025em]">You might also <span className="italic">love.</span></h2>
          <Link to="/collections" className="ink-link text-[14px] font-medium">See all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {recs.map((r) => (
            <Link key={r.slug} to={`/collection/${r.slug}`} className="group block rounded-2xl overflow-hidden bg-[#F3EADA] lift" data-testid={`related-${r.slug}`} data-hover="true">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={`/products/${r.slug}-kid.jpg`} alt={r.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]" loading="lazy" />
              </div>
              <div className="px-4 py-3 flex items-center justify-between">
                <h3 className="font-serif text-xl">{r.name}</h3>
                <span className="text-[12px] text-[#595955]">{PRICE}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#FAFAF7]/95 backdrop-blur border-t border-[#2C2C2A]/10 px-4 py-3 safe-bottom flex items-center gap-3" data-testid="mobile-sticky-cta">
        <div className="flex flex-col">
          <span className="font-serif text-lg leading-none">{c.name}</span>
          <span className="text-[12px] text-[#595955]">{PRICE}</span>
        </div>
        <a
          href={whatsappLink(buyMsg)}
          target="_blank" rel="noreferrer"
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-[14px] font-medium"
          data-testid="mobile-buy-cta"
        >
          <WhatsAppIcon /> Buy on WhatsApp
        </a>
      </div>
    </div>
  );
}
